import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Car, CalendarCheck, Users, DollarSign, Plus, Pencil, Trash2, LogOut, Clock, PhoneCall, Mail, MessageSquare, Phone, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const statusStyles = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  completed: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
};

const cleanPhone = (p) => (p || "").replace(/[^\d+]/g, "");

function QuickContact({ phone, email, idKey }) {
  const btn = "flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-medium tracking-wide transition-colors";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`quick-contact-${idKey}`}>
      {phone && (
        <>
          <a href={`sms:${cleanPhone(phone)}`} data-testid={`quick-text-${idKey}`}
            className={`${btn} bg-[#D4AF37] text-black hover:bg-[#E0BC4A]`}>
            <MessageSquare size={13} strokeWidth={1.8} /> Text
          </a>
          <a href={`tel:${cleanPhone(phone)}`} data-testid={`quick-call-${idKey}`}
            className={`${btn} border border-[#D4AF37] gold-text hover:bg-[#D4AF37] hover:text-black`}>
            <Phone size={13} strokeWidth={1.8} /> Call
          </a>
        </>
      )}
      {email && (
        <a href={`mailto:${email}`} data-testid={`quick-email-${idKey}`}
          className={`${btn} border border-white/15 text-zinc-300 hover:border-white/40`}>
          <Mail size={13} strokeWidth={1.8} /> Email
        </a>
      )}
    </div>
  );
}

const emptyVehicle = {
  name: "", category: "sedan", description: "", capacity: 3, luggage: 3,
  price_per_hour: 100, image_url: "", features: "", available: true,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [leads, setLeads] = useState([]);
  const [rates, setRates] = useState({ airport: [], local: [] });
  const [savingRates, setSavingRates] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [vForm, setVForm] = useState(emptyVehicle);
  const [saving, setSaving] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/bookings").then((r) => { hasFetchedRef.current = true; setBookings(r.data); }).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
  };

  useEffect(() => { loadAll(); }, []);

  // Live new-quote alerts: request browser notification permission + poll every 30s.
  const prevUnreadRef = useRef(null);
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const iv = setInterval(loadAll, 30000);
    return () => {
      clearInterval(iv);
      document.title = "Aspen Executive Limo";
    };
  }, []);

  // Update tab badge + fire a browser notification when unread count rises.
  useEffect(() => {
    const unread = bookings.filter((b) => !b.read).length;
    document.title = unread > 0 ? `(${unread}) Admin · Aspen Executive Limo` : "Admin · Aspen Executive Limo";
    if (!hasFetchedRef.current) return;
    if (prevUnreadRef.current === null) {
      prevUnreadRef.current = unread;
      return;
    }
    if (unread > prevUnreadRef.current) {
      const diff = unread - prevUnreadRef.current;
      const label = `${diff} new quote request${diff > 1 ? "s" : ""}`;
      toast.success(`${label} received`);
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const n = new Notification("New quote request", {
          body: `${label} just came in. Click to view.`,
          icon: "/favicon.ico",
          tag: "aspen-new-quote",
        });
        n.onclick = () => { window.focus(); setTab("bookings"); };
      }
    }
    prevUnreadRef.current = unread;
  }, [bookings]);

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}.`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openNew = () => { setEditing(null); setVForm(emptyVehicle); setDialogOpen(true); };
  const openEdit = (v) => {
    setEditing(v.id);
    setVForm({ ...v, features: v.features.join(", ") });
    setDialogOpen(true);
  };

  const saveVehicle = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...vForm,
      capacity: Number(vForm.capacity),
      luggage: Number(vForm.luggage),
      price_per_hour: Number(vForm.price_per_hour),
      features: vForm.features.split(",").map((f) => f.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/vehicles/${editing}`, payload);
        toast.success("Vehicle updated.");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehicle added.");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Vehicle deleted.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const setLeadStatus = async (id, status) => {
    try {
      await api.put(`/leads/${id}/status`, { status });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const markRead = async (id) => {
    try {
      await api.put(`/bookings/${id}/read`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateRateRow = (section, idx, field, value) => {
    setRates((prev) => ({
      ...prev,
      [section]: prev[section].map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
    }));
  };
  const addRateRow = (section) => setRates((prev) => ({ ...prev, [section]: [...prev[section], { from: "", to: "", rate: 0 }] }));
  const removeRateRow = (section, idx) => setRates((prev) => ({ ...prev, [section]: prev[section].filter((_, i) => i !== idx) }));
  const saveRates = async () => {
    setSavingRates(true);
    try {
      const clean = (arr) => arr
        .filter((r) => r.from?.trim() && r.to?.trim())
        .map((r) => ({ from: r.from.trim(), to: r.to.trim(), rate: Number(r.rate) || 0 }));
      await api.put("/rates", { airport: clean(rates.airport), local: clean(rates.local) });
      toast.success("Rates saved. Live on the homepage and Area Rates page.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingRates(false);
    }
  };

  const unreadCount = bookings.filter((b) => !b.read).length;

  const inputCls = "bg-[#1a1a1d] border-white/15 text-white focus-visible:ring-[#D4AF37] rounded-sm";
  const statCards = stats ? [
    { label: "Total Bookings", value: stats.total_bookings, icon: CalendarCheck },
    { label: "Pending", value: stats.pending, icon: Clock },
    { label: "Confirmed", value: stats.confirmed, icon: CalendarCheck },
    { label: "Vehicles", value: stats.total_vehicles, icon: Car },
    { label: "Customers", value: stats.total_customers, icon: Users },
    { label: "Revenue", value: `$${stats.revenue}`, icon: DollarSign },
    { label: "New Leads", value: stats.new_leads ?? 0, icon: PhoneCall },
  ] : [];

  const navItems = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "bookings", label: "Bookings", icon: CalendarCheck },
    { key: "fleet", label: "Fleet", icon: Car },
    { key: "leads", label: "Leads", icon: PhoneCall },
    { key: "rates", label: "Rates", icon: DollarSign },
    { key: "analytics", label: "Analytics", icon: BarChart3, route: "/admin/analytics" },
  ];

  const renderRateSection = (title, section) => (
    <div className="mb-12" data-testid={`rates-section-${section}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl font-light">{title}</h2>
        <button onClick={() => addRateRow(section)} data-testid={`add-rate-${section}`} className="text-sm flex items-center gap-1.5 border border-white/15 rounded-sm px-3 py-1.5 text-zinc-300 hover:border-white/40 transition-colors">
          <Plus size={14} /> Add Row
        </button>
      </div>
      <div className="hidden sm:grid grid-cols-[1fr_1fr_130px_44px] gap-3 px-1 mb-2 text-[11px] tracking-label uppercase text-zinc-500">
        <span>From</span><span>To</span><span>Flat Rate ($)</span><span></span>
      </div>
      <div className="space-y-3">
        {rates[section].map((row, idx) => (
          <div key={idx} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_130px_44px] gap-3 items-center">
            <Input value={row.from} onChange={(e) => updateRateRow(section, idx, "from", e.target.value)} placeholder="From" className={inputCls} data-testid={`rate-${section}-from-${idx}`} />
            <Input value={row.to} onChange={(e) => updateRateRow(section, idx, "to", e.target.value)} placeholder="To" className={inputCls} data-testid={`rate-${section}-to-${idx}`} />
            <Input type="number" value={row.rate} onChange={(e) => updateRateRow(section, idx, "rate", e.target.value)} placeholder="0" className={inputCls} data-testid={`rate-${section}-rate-${idx}`} />
            <button onClick={() => removeRateRow(section, idx)} data-testid={`remove-rate-${section}-${idx}`} className="flex items-center justify-center border border-red-500/30 text-red-400 rounded-sm py-2.5 hover:bg-red-500/10 transition-colors">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {rates[section].length === 0 && <p className="text-zinc-500 text-sm">No rows yet — click "Add Row".</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/10 bg-[#0d0d0f] hidden md:flex flex-col fixed h-screen">
        <div className="p-6 border-b border-white/10">
          <Logo imgClass="h-9 w-9" textClass="text-xl" />
          <p className="text-xs tracking-label uppercase text-zinc-600 mt-2">Admin Console</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((n) => (
            <button
              key={n.key}
              onClick={() => (n.route ? navigate(n.route) : setTab(n.key))}
              data-testid={`admin-tab-${n.key}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-all ${
                tab === n.key ? "bg-[#D4AF37] text-black font-medium" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <n.icon size={18} strokeWidth={1.5} /> {n.label}
              {n.key === "bookings" && unreadCount > 0 && (
                <span data-testid="nav-bookings-unread" className={`ml-auto text-[11px] font-semibold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center ${tab === n.key ? "bg-black text-[#D4AF37]" : "bg-[#D4AF37] text-black"}`}>{unreadCount}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <button onClick={handleLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-all">
            <LogOut size={18} strokeWidth={1.5} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64">
        {/* Mobile top tabs */}
        <div className="md:hidden flex gap-2 p-4 border-b border-white/10 overflow-x-auto">
          {navItems.map((n) => (
            <button key={n.key} onClick={() => (n.route ? navigate(n.route) : setTab(n.key))} className={`px-4 py-2 rounded-sm text-sm whitespace-nowrap flex items-center gap-2 ${tab === n.key ? "bg-[#D4AF37] text-black" : "text-zinc-400 border border-white/10"}`}>{n.label}{n.key === "bookings" && unreadCount > 0 && (<span className={`text-[10px] font-semibold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center ${tab === n.key ? "bg-black text-[#D4AF37]" : "bg-[#D4AF37] text-black"}`}>{unreadCount}</span>)}</button>
          ))}
        </div>

        <div className="p-6 md:p-10">
          {tab === "overview" && (
            <div data-testid="admin-overview">
              <h1 className="font-heading text-4xl font-light tracking-tight mb-2">Dashboard Overview</h1>
              <p className="text-zinc-500 font-light mb-10">Welcome back, {user?.name}.</p>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                {statCards.map((s) => (
                  <div key={s.label} data-testid={`admin-stat-${s.label}`} className="bg-[#121214] border border-white/10 rounded-sm p-6 hover:border-[#D4AF37]/30 transition-colors">
                    <s.icon size={22} strokeWidth={1.5} className="gold-text mb-4" />
                    <div className="font-heading text-4xl">{s.value}</div>
                    <div className="text-xs tracking-label uppercase text-zinc-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>

              <h2 className="font-heading text-2xl font-light mt-14 mb-5">Recent Bookings</h2>
              <div className="space-y-3">
                {bookings.slice(0, 5).map((b) => (
                  <div key={b.id} className="bg-[#121214] border border-white/10 rounded-sm p-4 flex justify-between items-center">
                    <div>
                      <p className="font-normal">{b.vehicle_name} <span className="text-zinc-500 text-sm">· {b.user_email}</span></p>
                      <p className="text-zinc-500 text-sm">{b.pickup_date} at {b.pickup_time}</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-sm border capitalize ${statusStyles[b.status]}`}>{b.status}</span>
                  </div>
                ))}
                {bookings.length === 0 && <p className="text-zinc-500">No bookings yet.</p>}
              </div>
            </div>
          )}

          {tab === "bookings" && (
            <div data-testid="admin-bookings">
              <h1 className="font-heading text-4xl font-light tracking-tight mb-10">Manage Bookings</h1>
              {bookings.length === 0 ? (
                <p className="text-zinc-500">No bookings yet.</p>
              ) : (
                <div className="space-y-4">
                  {bookings.map((b) => (
                    <div key={b.id} data-testid={`admin-booking-${b.id}`} className={`bg-[#121214] border rounded-sm p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${!b.read ? "border-[#D4AF37]/60 ring-1 ring-[#D4AF37]/30" : "border-white/10"}`}>
                      <div className="flex gap-4 items-center">
                        <img src={b.vehicle_image} alt={b.vehicle_name} className="w-20 h-16 object-cover rounded-sm hidden sm:block" />
                        <div className="space-y-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-heading text-xl">{b.vehicle_name}</h3>
                            {!b.read && <span data-testid={`booking-new-badge-${b.id}`} className="text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-sm bg-[#D4AF37] text-black">NEW</span>}
                          </div>
                          <p className="text-zinc-400 text-sm">{b.contact_name || b.user_email} · {b.passengers} pax · {b.hours}h</p>
                          <p className="text-zinc-500 text-sm">{b.pickup_location} → {b.dropoff_location}</p>
                          <p className="text-zinc-500 text-sm">{b.pickup_date} at {b.pickup_time}</p>
                          {b.contact_phone && <p className="text-zinc-400 text-sm flex items-center gap-2"><PhoneCall size={13} className="gold-text" /> {b.contact_phone}</p>}
                          {b.notes && <p className="text-zinc-600 text-xs italic">“{b.notes}”</p>}
                          <div className="pt-2">
                            <QuickContact phone={b.contact_phone} email={b.contact_email || b.user_email} idKey={b.id} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {!b.read && (
                          <button onClick={() => markRead(b.id)} data-testid={`mark-read-${b.id}`} className="text-xs text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 rounded-sm px-3 py-1.5 transition-colors whitespace-nowrap">
                            Mark read
                          </button>
                        )}
                        <span className="gold-text font-mono">${b.total_price}</span>
                        <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                          <SelectTrigger data-testid={`booking-status-select-${b.id}`} className={`w-40 ${inputCls}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1d] border-white/15 text-white">
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "fleet" && (
            <div data-testid="admin-fleet">
              <div className="flex justify-between items-center mb-10">
                <h1 className="font-heading text-4xl font-light tracking-tight">Manage Fleet</h1>
                <button onClick={openNew} data-testid="add-vehicle-btn" className="bg-[#D4AF37] text-black hover:bg-[#E0BC4A] transition-all rounded-sm px-5 py-2.5 font-medium flex items-center gap-2">
                  <Plus size={18} /> Add Vehicle
                </button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((v) => (
                  <div key={v.id} data-testid={`admin-vehicle-${v.id}`} className="bg-[#121214] border border-white/10 rounded-sm overflow-hidden">
                    <img src={v.image_url} alt={v.name} className="w-full h-40 object-cover" />
                    <div className="p-5">
                      <p className="text-xs tracking-label uppercase text-zinc-500">{v.category}</p>
                      <h3 className="font-heading text-xl mt-1">{v.name}</h3>
                      <p className="gold-text font-mono mt-2">${v.price_per_hour}/hr</p>
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`} className="flex-1 flex items-center justify-center gap-1.5 text-sm border border-white/15 rounded-sm py-2 text-zinc-300 hover:border-white/40 transition-colors">
                          <Pencil size={14} /> Edit
                        </button>
                        <button onClick={() => deleteVehicle(v.id)} data-testid={`delete-vehicle-${v.id}`} className="flex items-center justify-center gap-1.5 text-sm border border-red-500/30 text-red-400 rounded-sm py-2 px-3 hover:bg-red-500/10 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "leads" && (
            <div data-testid="admin-leads">
              <h1 className="font-heading text-4xl font-light tracking-tight mb-10">Callback Requests</h1>
              {leads.length === 0 ? (
                <div className="bg-[#121214] border border-white/10 rounded-sm p-16 text-center" data-testid="no-leads">
                  <PhoneCall size={40} strokeWidth={1} className="mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-400 font-light">No callback requests yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {leads.map((l) => (
                    <div key={l.id} data-testid={`admin-lead-${l.id}`} className="bg-[#121214] border border-white/10 rounded-sm p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-heading text-xl">{l.name}</h3>
                          <span className={`text-xs px-3 py-1 rounded-sm border capitalize ${l.status === "new" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : l.status === "contacted" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}>{l.status}</span>
                        </div>
                        <p className="text-zinc-400 text-sm flex items-center gap-2"><PhoneCall size={14} className="gold-text" /> {l.phone}</p>
                        {l.email && <p className="text-zinc-400 text-sm flex items-center gap-2"><Mail size={14} className="gold-text" /> {l.email}</p>}
                        {l.message && <p className="text-zinc-500 text-sm italic">“{l.message}”</p>}
                        <div className="pt-2">
                          <QuickContact phone={l.phone} email={l.email} idKey={l.id} />
                        </div>
                      </div>
                      <Select value={l.status} onValueChange={(v) => setLeadStatus(l.id, v)}>
                        <SelectTrigger data-testid={`lead-status-select-${l.id}`} className={`w-40 ${inputCls}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1d] border-white/15 text-white">
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "rates" && (
            <div data-testid="admin-rates">
              <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
                <h1 className="font-heading text-4xl font-light tracking-tight">Manage Rates</h1>
                <button onClick={saveRates} disabled={savingRates} data-testid="save-rates-btn" className="inline-flex items-center justify-center bg-[#D4AF37] text-black hover:bg-[#E0BC4A] disabled:opacity-50 transition-all rounded-sm px-[3px] py-[1px] font-medium">
                  {savingRates ? "Saving…" : "Save Changes"}
                </button>
              </div>
              <p className="text-zinc-500 font-light mb-10 max-w-2xl">Edits here update both the homepage Airport Rates panel and the public Area Rates page instantly after saving.</p>
              {renderRateSection("Airport Rates", "airport")}
              {renderRateSection("Area Rates", "local")}
            </div>
          )}
        </div>
      </main>

      {/* Vehicle dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/15 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">{editing ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveVehicle} className="space-y-4">
            <div>
              <Label className="text-zinc-400 text-xs mb-1.5 block">Name</Label>
              <Input data-testid="vehicle-form-name" required className={inputCls} value={vForm.name} onChange={(e) => setVForm({ ...vForm, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">Category</Label>
                <Select value={vForm.category} onValueChange={(v) => setVForm({ ...vForm, category: v })}>
                  <SelectTrigger data-testid="vehicle-form-category" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1d] border-white/15 text-white">
                    {["sedan", "suv", "limousine", "van", "luxury"].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">Price / hour ($)</Label>
                <Input data-testid="vehicle-form-price" type="number" required className={inputCls} value={vForm.price_per_hour} onChange={(e) => setVForm({ ...vForm, price_per_hour: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">Capacity</Label>
                <Input data-testid="vehicle-form-capacity" type="number" required className={inputCls} value={vForm.capacity} onChange={(e) => setVForm({ ...vForm, capacity: e.target.value })} />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">Luggage</Label>
                <Input data-testid="vehicle-form-luggage" type="number" required className={inputCls} value={vForm.luggage} onChange={(e) => setVForm({ ...vForm, luggage: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1.5 block">Image URL</Label>
              <Input data-testid="vehicle-form-image" required className={inputCls} value={vForm.image_url} onChange={(e) => setVForm({ ...vForm, image_url: e.target.value })} />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1.5 block">Description</Label>
              <Textarea data-testid="vehicle-form-description" required rows={2} className={inputCls} value={vForm.description} onChange={(e) => setVForm({ ...vForm, description: e.target.value })} />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs mb-1.5 block">Features (comma separated)</Label>
              <Input data-testid="vehicle-form-features" className={inputCls} placeholder="Leather, WiFi, Bar" value={vForm.features} onChange={(e) => setVForm({ ...vForm, features: e.target.value })} />
            </div>
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="vehicle-form-submit" className="inline-flex items-center justify-center bg-[#D4AF37] text-black hover:bg-[#E0BC4A] disabled:opacity-50 transition-all rounded-sm px-[3px] py-[1px] font-medium">
                {saving ? "Saving…" : editing ? "Update" : "Add Vehicle"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
